import React, { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/services/api';
import { ToastProvider } from '@/components/ui/toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Layouts
import { MainLayout } from '@/components/layouts/MainLayout';
import { AuthLayout } from '@/components/layouts/AuthLayout';

// Pages
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { EmailVerifyPage } from '@/pages/auth/EmailVerifyPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { FlightListPage } from '@/pages/flights/FlightListPage';
import { FlightCalendarPage } from '@/pages/flights/FlightCalendarPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { PushSettingsPage } from '@/pages/settings/PushSettingsPage';
import { VaultSettingsPage } from '@/pages/settings/VaultSettingsPage';
import { AISettingsPage } from '@/pages/settings/AISettingsPage';
import { RedeemPage } from '@/pages/subscription/RedeemPage';
import SubscriptionAdminPage from '@/pages/admin/SubscriptionAdminPage';
import { SyncCenterPage } from '@/pages/sync/SyncCenterPage';

// FSOP Pages (new unified module)
import FsopDashboardPage from '@/pages/fsop/FsopDashboardPage';
import FsopFlightLogsPage from '@/pages/fsop/FsopFlightLogsPage';
import FsopViewConfigPage from '@/pages/fsop/FsopViewConfigPage';
import FsopRouteMapPage from '@/pages/fsop/FsopRouteMapPage';

// Note Pages
import NotesListPage from '@/pages/notes/NotesListPage';
import NoteEditPage from '@/pages/notes/NoteEditPage';
import NoteDetailPage from '@/pages/notes/NoteDetailPage';
import NotesPlazaPage from '@/pages/notes/NotesPlazaPage';
import PlazaNoteDetailPage from '@/pages/notes/PlazaNoteDetailPage';
import CareerPage from '@/pages/career/CareerPage';

// 应用启动时通过 httpOnly Cookie 静默刷新 access token
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { token, hasHydrated, setToken, logout, setLoading } = useAuthStore();

  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      if (!hasHydrated) {
        return;
      }

      // 如果已经有内存中的 access token，无需刷新
      if (token) {
        setLoading(false);
        return;
      }

      try {
        const response = await authApi.refresh();
        const { access } = response.data;
        if (mounted) {
          setToken(access);
        }
      } catch {
        if (mounted) {
          logout();
        }
      }
    }

    initAuth();

    return () => {
      mounted = false;
    };
  }, [hasHydrated, token, setToken, logout, setLoading]);

  return <>{children}</>;
}

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">加载中...</div>;
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Admin Route Component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">加载中...</div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.isStaff) return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Home route: show landing page for guests, redirect to /fsop for logged-in users
const HomeRoute = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">加载中...</div>;
  }
  return isAuthenticated ? <Navigate to="/fsop" replace /> : <LandingPage />;
};

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthInitializer>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/welcome" element={<Navigate to="/" replace />} />

            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<EmailVerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Route>

            {/* Protected routes without MainLayout */}
            <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
              <Route path="/redeem" element={<RedeemPage />} />
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/flights" element={<FlightListPage />} />
              <Route path="/flights/calendar" element={<FlightCalendarPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/push" element={<PushSettingsPage />} />
              <Route path="/settings/vault" element={<VaultSettingsPage />} />
              <Route path="/settings/ai" element={<AISettingsPage />} />
              <Route path="/sync" element={<SyncCenterPage />} />

              {/* FSOP 模块 */}
              <Route path="/fsop" element={<FsopDashboardPage />} />
              <Route path="/fsop/flightlogs" element={<FsopFlightLogsPage />} />
              <Route path="/fsop/flightlogs/views" element={<FsopViewConfigPage />} />
              <Route path="/fsop/routeMap" element={<FsopRouteMapPage />} />

              {/* Redirects for removed pages */}
              <Route path="/sync-logs" element={<Navigate to="/sync" replace />} />
              <Route path="/settings/subscription" element={<Navigate to="/sync" replace />} />
              <Route path="/fsop/credentials" element={<Navigate to="/sync" replace />} />
              <Route path="/fsop/flightlogs/sync" element={<Navigate to="/sync" replace />} />

              {/* 笔记模块 */}
              <Route path="/notes" element={<NotesListPage />} />
              <Route path="/notes/new" element={<NoteEditPage />} />
              <Route path="/notes/plaza" element={<NotesPlazaPage />} />
              <Route path="/notes/plaza/:id" element={<PlazaNoteDetailPage />} />
              <Route path="/notes/:id" element={<NoteDetailPage />} />
              <Route path="/notes/:id/edit" element={<NoteEditPage />} />
              <Route path="/notes/by-route" element={<Navigate to="/notes" replace />} />
              <Route path="/notes/by-airport" element={<Navigate to="/notes" replace />} />
              <Route path="/notes/by-crew" element={<Navigate to="/notes" replace />} />

              {/* 职业生涯 */}
              <Route path="/career" element={<CareerPage />} />

              {/* Admin */}
              <Route path="/admin/subscription" element={
                <AdminRoute>
                  <SubscriptionAdminPage />
                </AdminRoute>
              } />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthInitializer>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
